<template>
  <div data-component-group="filter-forms" data-component="user">
    <FiltersForm
      :has-filters="hasFilters"
      :is-global-country-filter="isInternalUsersPage"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <Can action="see-user-search" subject="filter">
        <v-text-field
          clearable
          v-model="search"
          type="search"
          label="Search"
          prepend-inner-icon="search"
        />
      </Can>

      <v-autocomplete
        clearable
        v-if="isStatusFilterShown"
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        :return-object="true"
        item-title="name"
        label="Status"
        @focus="onFocusStatus"
      />

      <Can action="see-user-role" subject="filter">
        <v-autocomplete
          clearable
          v-model="role"
          :items="roles"
          prepend-inner-icon="supervisor_account"
          :return-object="true"
          item-title="name"
          label="Role"
          @focus="onFocusRoles"
        />
      </Can>
    </FiltersForm>
  </div>
</template>

<script>
import { v1 as uuidv1 } from 'uuid';
import FiltersForm from './FiltersBase';
import filterUrlSync from '@/mixins/filterUrlSync';
import { isNum, isStr, isNonEmptyArr, debounce, escapeSearch } from '@/utils';

const debouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

// TODO: Translate component

export default {
  mixins: [filterUrlSync],
  components: {
    FiltersForm,
  },
  emits: ['clearFilters', 'filter'],
  data() {
    return {
      urlConfig: [
        { field: 'search', urlKey: 'search', type: 'string', can: { action: 'see-user-search', subject: 'filter' } },
        {
          field: 'status', urlKey: 'status', type: 'number', prop: 'id',
          fetchMethod: 'getUserStatuses', itemsField: 'statuses', when: 'isStatusFilterShown',
        },
        {
          field: 'role', urlKey: 'role', type: 'string', prop: 'slug',
          fetchMethod: 'getUserRoles', itemsField: 'roles',
          can: { action: 'see-user-role', subject: 'filter' },
        },
        // Only the internal-users page sends countries (see onFilter).
        { field: 'selectedCountries', urlKey: 'countries', type: 'string[]', when: 'isInternalUsersPage' },
      ],
      search: null,
      role: null,
      roles: [],
      status: null,
      selectedCountries: [],
    };
  },
  computed: {
    hasFilters() {
      const { search, role = {}, status = {} } = this;

      return (
        !!search ||
        isStr(role?.slug) ||
        isNum(status?.id) ||
        (isNonEmptyArr(this.selectedCountries) && this.isInternalUsersPage)
      );
    },
    statuses() {
      return this.$store.getters['businessUsers/statuses'];
    },
    isStatusFilterShown() {
      return !this.isPendingUsersPage;
    },
    isPendingUsersPage() {
      return this.$route.matched?.[0]?.name === 'users-pending';
    },
    isInternalUsersPage() {
      // Dialog routes (internal-users-new, internal-users-edit) are children
      // of 'internal-users', so $route.name changes while this filter form
      // stays mounted. Check the top-level matched route instead, so opening
      // the add/edit dialog doesn't flip this to false.
      return this.$route.matched?.[0]?.name === 'internal-users';
    },
  },
  methods: {
    getUserStatuses() {
      if (this.statuses.length) {
        return;
      }

      return this.$store.dispatch('businessUsers/getStatuses');
    },
    getUserRoles() {
      const params = {};

      if (this.isInternalUsersPage) {
        params.type = 1;
      } else {
        params.type = 2;
      }

      return this.$store.dispatch('users/getRolesSpecified', params)
        .then(res => {
          this.roles = res
        })
        .catch(err => err);
    },
    onFilter() {
      const { search, role = {}, status = {}, selectedCountries } = this;
      const formattedSearch = escapeSearch(search);

      const args = {
        search: formattedSearch,
        role: role?.slug,
        status: status?.id,
      };

      if (this.isInternalUsersPage) {
        args.allowed_countries = selectedCountries;
      }

      this.syncFiltersToUrl();


      this.$emit('filter', args);
    },
    onClearFilters() {
      this.search = null;
      this.role = null;
      this.status = null;

      this.clearFiltersUrl();


      this.$emit('clearFilters');
    },
    onFocusStatus() {
      if (this.statuses.length === 0) {
        this.getUserStatuses();
      }
    },
    onFocusRoles() {
      if (this.roles.length === 0) {
        this.getUserRoles();
      }
    },
  },
};
</script>
